import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type { ToggleProps } from './Toggle.types';
import {
  toggleKnobVariants,
  toggleRootVariants,
  toggleTrackVariants,
} from './Toggle.variants';

/**
 * Cross-platform on/off Toggle (switch). Renders identically on web and React
 * Native. Mirrors the Figma "Flashka Design System" frame `1665:14429`
 * ("Toggle"). See `Toggle.variants.ts` for the per-state spec + anatomy.
 *
 * Controlled-only — the caller owns the boolean:
 *
 * @example
 * ```tsx
 * import { Toggle } from '@flashka/shared/ui';
 *
 * const [on, setOn] = useState(false);
 * <Toggle checked={on} onCheckedChange={setOn} accessibilityLabel="Dark mode" />
 *
 * // Sized + disabled
 * <Toggle size="l" checked disabled onCheckedChange={() => {}} />
 * ```
 */
export function Toggle({
  checked = false,
  onCheckedChange,
  size = 'm',
  disabled = false,
  interactive = true,
  haptic = 'selection',
  className,
  trackClassName,
  knobClassName,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
  onHoverIn,
  onHoverOut,
}: ToggleProps) {
  // Decorative track + knob — the interactive Pressable (or, in presentational
  // mode, the owning row) carries the switch role + label, so these layers stay
  // hidden from assistive tech (`aria-hidden` on web; plain RN Views with no
  // role aren't announced).
  const layers = (
    <>
      <View
        webProps={{ 'aria-hidden': true }}
        className={cn(toggleTrackVariants({ size, active: checked }), trackClassName)}
      />
      <View
        webProps={{ 'aria-hidden': true }}
        className={cn(toggleKnobVariants({ size, active: checked }), knobClassName)}
      />
    </>
  );

  // Presentational mode — a pure visual indicator (no role, focus, press, or
  // haptic) for embedding inside a larger clickable card/row that owns the
  // switch semantics. Same contract as Radio / Checkbox.
  if (!interactive) {
    return (
      <View
        className={cn(
          toggleRootVariants({ size, active: checked }),
          'web:pointer-events-none',
          className,
        )}
        testID={testID}
        webProps={{ 'aria-hidden': true, ...(webProps ?? {}) }}
        nativeProps={{ accessible: false, ...(nativeProps ?? {}) }}>
        {layers}
      </View>
    );
  }

  const handlePress = (event: PressEvent) => {
    if (!disabled) onCheckedChange?.(!checked, event);
  };

  // Announce switch + state to assistive tech on both platforms. `role="switch"`
  // comes from `accessibilityRole`; `aria-checked` (web) / `accessibilityState`
  // (native) carry the on/off value. Caller props win on key overlap.
  const mergedWebProps = { 'aria-checked': checked, ...(webProps ?? {}) };
  const mergedNativeProps = {
    accessibilityState: { checked, disabled },
    ...(nativeProps ?? {}),
  };

  return (
    <Pressable
      className={cn(toggleRootVariants({ size, active: checked }), className)}
      disabled={disabled}
      haptic={haptic}
      onPress={handlePress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityRole="switch"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={mergedWebProps}
      nativeProps={mergedNativeProps}>
      {layers}
    </Pressable>
  );
}
(Toggle as { displayName?: string }).displayName = 'Toggle';
